import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	CanDeactivate,
	Router,
	RouterStateSnapshot,
} from '@angular/router';
import { Observable, map, tap } from 'rxjs';

import { AuthFacade } from '@facades/auth.facade';
import { TenantsFacade } from '@facades/tenants.facade';
import { SellerOrderComponent } from '@pages/dashboard/seller-order/seller-order.component';
import { canCreatePortalOrders } from '@utils/portal-orders-permissions.util';

/**
 * Allows access when the tenant has portalOrders and the user may create
 * portal orders (SELLER/APPLICATION_ADMIN + portal.createOrders, with legacy
 * and admin exceptions).
 */
@Injectable({
	providedIn: 'root',
})
export class SellerOrderGuard implements CanActivate {
	constructor(
		private _router: Router,
		private authFacade: AuthFacade,
		private tenantsFacade: TenantsFacade
	) {}

	canActivate(
		_route: ActivatedRouteSnapshot,
		_state: RouterStateSnapshot
	): Observable<boolean> {
		return this.authFacade.authState$.pipe(
			map(({ user }) =>
				canCreatePortalOrders(user, {
					portalOrdersEnabled: this.tenantsFacade.hasFeatures('portalOrders'),
				})
			),
			tap(allowed => {
				if (!allowed) this._router.navigate(['/']);
			})
		);
	}
}

/** Asks before leaving a seller order that still has a client or items. */
@Injectable({
	providedIn: 'root',
})
export class SellerOrderLeaveGuard
	implements CanDeactivate<SellerOrderComponent>
{
	canDeactivate(component: SellerOrderComponent) {
		return component.confirmLeave();
	}
}
